import { Button, useToast } from '@citizen-walk/shared-ui';
import { useEffect, useState } from 'react';
import { useDeviceTransferConsume } from './useDeviceTransfer';

export interface DeviceTransferConsumeFormProps {
  /** 引継成功時のコールバック (任意、UI で reload 等の操作)。 */
  onConsumed?: (result: { transferredCount: number; accessibleDistricts: string[] }) => void;
  /**
   * デフォルトは success 後に `window.location.reload()` を行う。
   * テスト等で挙動を抑止したい場合は false を渡す。
   */
  reloadOnSuccess?: boolean;
}

const TOKEN_LENGTH = 8;

/**
 * デバイス間引継コード入力フォーム (Sprint BG-DataSettings 8、GAP U-C9)。
 *
 * 旧 `DeviceTransferModal.tsx` の「PWA 側で入力する」UI は v1 では DistrictHomePage に inline で
 * 配置されていた。v2 では本 component を AppHome の standalone + 空 memberships 検出時に表示する
 * (`isStandalone() && memberships.empty`)。
 *
 * UX:
 *   - 8 文字 token 入力 (大文字化 + ハイフン/空白除去 自動正規化)
 *   - 「引き継ぐ」ボタン → callable → 成功時 reload (Custom Claims 反映 + memberships が見える)
 *   - 失敗時は専用文言で error 表示 (useDeviceTransferConsume 内で normalize 済)
 */
export function DeviceTransferConsumeForm({
  onConsumed,
  reloadOnSuccess = true,
}: DeviceTransferConsumeFormProps) {
  const { state, call } = useDeviceTransferConsume();
  const { toast } = useToast();
  const [draft, setDraft] = useState('');

  const normalized = draft.toUpperCase().replace(/[\s-]/g, '');
  const canSubmit = normalized.length === TOKEN_LENGTH && state.status !== 'pending';

  const status = state.status;
  const result = state.status === 'success' ? state.data : null;
  const errorMsg = state.status === 'error' ? state.error.message : null;

  useEffect(() => {
    if (status === 'success' && result) {
      toast('success', `引継ぎ成功: ${result.transferredCount} 地区分をコピーしました`);
      onConsumed?.({
        transferredCount: result.transferredCount,
        accessibleDistricts: result.accessibleDistricts,
      });
      if (reloadOnSuccess && typeof window !== 'undefined') {
        // 短い遅延を入れて toast の視認時間を確保 (旧 v1 にはなかった改善)
        setTimeout(() => window.location.reload(), 800);
      }
    } else if (status === 'error') {
      toast('error', errorMsg ?? '引継ぎに失敗しました');
    }
  }, [status, result, errorMsg, toast, onConsumed, reloadOnSuccess]);

  function handleSubmit() {
    if (!canSubmit) return;
    void call({ token: normalized });
  }

  return (
    <section
      aria-labelledby="device-transfer-consume-heading"
      className="bg-slate-50 border border-slate-200 rounded-lg p-4"
    >
      <h2 id="device-transfer-consume-heading" className="text-base font-bold text-slate-900 mb-2">
        🔗 ブラウザから引き継ぎ
      </h2>
      <p className="text-xs text-slate-600 mb-3">
        他端末で発行した 8 文字の引継コードを入力すると、参加中の地区を一括でコピーします。
      </p>
      {/* R-70 UI 注意喚起: 引き継ぎ直後の他タブは編集権限の反映が遅れる (読取は遅延なし)。 */}
      <p className="text-xs text-slate-500 mb-3">
        引き継ぎ後、同じ端末で別のタブを開いている場合は、投稿やカードの編集・削除を行う前に、そのタブを再読み込みしてください（閲覧はそのまま行えます）。
      </p>

      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="ABCD2345"
        maxLength={16}
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        aria-label="引継コード入力"
        className="w-full px-3 py-2 mb-2 border border-slate-300 rounded-lg text-lg font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-primary-400"
      />
      <p className="text-xs text-slate-400 mb-3">
        {normalized.length} / {TOKEN_LENGTH} 文字 (ハイフン・空白は自動除去)
      </p>

      {state.status === 'error' && (
        <p className="text-sm text-red-700 bg-red-50 p-3 rounded border border-red-200 mb-3">
          {state.error.message}
        </p>
      )}

      <Button
        variant="primary"
        size="md"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="w-full"
      >
        {state.status === 'pending' ? '引継ぎ中…' : '引き継ぐ'}
      </Button>
    </section>
  );
}

/**
 * PWA standalone mode を判定 (display-mode: standalone)。
 *
 * AppHome 側で `isStandalone() && memberships.empty` の条件で DeviceTransferConsumeForm を出す。
 * SSR / 古いブラウザ対策で matchMedia の有無を判定する。
 */
export function isStandalonePwa(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}
